import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  fetchPersonnelPeople,
  fetchPersonnelAbsences,
  fetchPersonnelPublicHolidays,
  fetchPersonnelHolidayWork,
  fetchPersonnelWorkDays,
} from "../api/personnelApi";

import {
  loadPersonnelResource,
  readPersonnelCache,
  subscribePersonnelCache,
} from "../utils/personnelCache";

import {
  getPersonnelYearRanges,
} from "../lib/prefetchPersonnel";

const readYearCache = (
  year
) => {
  const ranges =
    getPersonnelYearRanges(
      year
    );

  return {
    ranges,

    people:
      readPersonnelCache(
        "people",
        {}
      ),

    absences:
      readPersonnelCache(
        "absences",
        ranges.absences
      ),

    publicHolidays:
      readPersonnelCache(
        "publicHolidays",
        ranges.publicHolidays
      ),

    holidayWork:
      readPersonnelCache(
        "holidayWork",
        ranges.holidayWork
      ),

    workDays:
      readPersonnelCache(
        "workDays",
        ranges.workDays
      ),
  };
};

export default function usePersonnelCalendarData(
  year
) {
  const initial =
    readYearCache(
      year
    );

  const [
    people,
    setPeople,
  ] =
    useState(
      () =>
        initial.people
          ?.data ||
        []
    );

  const [
    absences,
    setAbsences,
  ] =
    useState(
      () =>
        initial.absences
          ?.data ||
        []
    );

  const [
    publicHolidays,
    setPublicHolidays,
  ] =
    useState(
      () =>
        initial.publicHolidays
          ?.data ||
        []
    );

  const [
    holidayWork,
    setHolidayWork,
  ] =
    useState(
      () =>
        initial.holidayWork
          ?.data ||
        []
    );

  const [
    workDays,
    setWorkDays,
  ] =
    useState(
      () =>
        initial.workDays
          ?.data ||
        []
    );

  const [
    loading,
    setLoading,
  ] =
    useState(
      () =>
        !(
          initial.people &&
          initial.absences &&
          initial.publicHolidays &&
          initial.holidayWork &&
          initial.workDays
        )
    );

  const [
    refreshing,
    setRefreshing,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      null
    );

  const requestRef =
    useRef(
      0
    );

  const realtimeRefreshTimerRef =
    useRef(
      null
    );

  const refresh =
    useCallback(
      async ({
        silent =
          true,
      } = {}) => {
        const requestId =
          requestRef.current +
          1;

        requestRef.current =
          requestId;

        const ranges =
          getPersonnelYearRanges(
            year
          );

        if (!silent) {
          setLoading(
            true
          );
        }

        setRefreshing(
          true
        );

        setError(
          null
        );

        const results =
          await Promise.allSettled([
            loadPersonnelResource({
              resource:
                "people",

              params: {},

              force:
                true,

              loader:
                () =>
                  fetchPersonnelPeople(),
            }),

            loadPersonnelResource({
              resource:
                "absences",

              params:
                ranges.absences,

              force:
                true,

              loader:
                () =>
                  fetchPersonnelAbsences(
                    ranges.absences
                  ),
            }),

            loadPersonnelResource({
              resource:
                "publicHolidays",

              params:
                ranges.publicHolidays,

              force:
                true,

              loader:
                () =>
                  fetchPersonnelPublicHolidays(
                    ranges.publicHolidays
                  ),
            }),

            loadPersonnelResource({
              resource:
                "holidayWork",

              params:
                ranges.holidayWork,

              force:
                true,

              loader:
                () =>
                  fetchPersonnelHolidayWork(
                    ranges.holidayWork
                  ),
            }),

            loadPersonnelResource({
              resource:
                "workDays",

              params:
                ranges.workDays,

              force:
                true,

              loader:
                () =>
                  fetchPersonnelWorkDays(
                    ranges.workDays
                  ),
            }),
          ]);

        if (
          requestRef.current !==
          requestId
        ) {
          return;
        }

        const [
          peopleResult,
          absencesResult,
          holidaysResult,
          holidayWorkResult,
          workDaysResult,
        ] =
          results;

        let failures =
          0;

        if (
          peopleResult.status ===
          "fulfilled"
        ) {
          setPeople(
            peopleResult.value
          );
        } else {
          failures +=
            1;
        }

        if (
          absencesResult.status ===
          "fulfilled"
        ) {
          setAbsences(
            absencesResult.value
          );
        } else {
          failures +=
            1;
        }

        if (
          holidaysResult.status ===
          "fulfilled"
        ) {
          setPublicHolidays(
            holidaysResult.value
          );
        } else {
          failures +=
            1;
        }

        if (
          holidayWorkResult.status ===
          "fulfilled"
        ) {
          setHolidayWork(
            holidayWorkResult.value
          );
        } else {
          failures +=
            1;
        }

        if (
          workDaysResult.status ===
          "fulfilled"
        ) {
          setWorkDays(
            workDaysResult.value
          );
        } else {
          failures +=
            1;
        }

        if (
          failures >
          0
        ) {
          setError(
            new Error(
              "Some personnel data could not be refreshed."
            )
          );
        }

        setLoading(
          false
        );

        setRefreshing(
          false
        );
      },
      [
        year,
      ]
    );

  useEffect(
    () => {
      const cached =
        readYearCache(
          year
        );

      setPeople(
        cached.people
          ?.data ||
        []
      );

      setAbsences(
        cached.absences
          ?.data ||
        []
      );

      setPublicHolidays(
        cached.publicHolidays
          ?.data ||
        []
      );

      setHolidayWork(
        cached.holidayWork
          ?.data ||
        []
      );

      setWorkDays(
        cached.workDays
          ?.data ||
        []
      );

      const hasFullCache =
        Boolean(
          cached.people &&
          cached.absences &&
          cached.publicHolidays &&
          cached.holidayWork &&
          cached.workDays
        );

      const hasStaleCache =
        Boolean(
          cached.people
            ?.isStale ||
          cached.absences
            ?.isStale ||
          cached.publicHolidays
            ?.isStale ||
          cached.holidayWork
            ?.isStale ||
          cached.workDays
            ?.isStale
        );

      setLoading(
        !hasFullCache
      );

      if (
        !hasFullCache ||
        hasStaleCache
      ) {
        refresh({
          silent:
            hasFullCache,
        });
      } else {
        setRefreshing(
          false
        );

        setError(
          null
        );
      }
    },
    [
      year,
      refresh,
    ]
  );

  useEffect(
    () => {
      const relevantResources =
        new Set([
          "people",
          "absences",
          "publicHolidays",
          "holidayWork",
          "workDays",
        ]);

      const unsubscribe =
        subscribePersonnelCache(
          (
            event
          ) => {
            if (
              !relevantResources.has(
                event.resource
              )
            ) {
              return;
            }

            if (
              event.stale ===
              true
            ) {
              const requestVersionAtEvent =
                requestRef.current;

              if (
                realtimeRefreshTimerRef.current
              ) {
                window.clearTimeout(
                  realtimeRefreshTimerRef.current
                );
              }

              realtimeRefreshTimerRef.current =
                window.setTimeout(
                  () => {
                    realtimeRefreshTimerRef.current =
                      null;

                    if (
                      requestRef.current !==
                      requestVersionAtEvent
                    ) {
                      return;
                    }

                    refresh({
                      silent:
                        true,
                    }).catch(
                      (
                        refreshError
                      ) => {
                        console.error(
                          "[PersonnelCalendar] realtime refresh failed:",
                          refreshError
                        );
                      }
                    );
                  },
                  60
                );

              return;
            }

            const cached =
              readYearCache(
                year
              );

            if (
              cached.people
            ) {
              setPeople(
                cached.people.data
              );
            }

            if (
              cached.absences
            ) {
              setAbsences(
                cached.absences.data
              );
            }

            if (
              cached.publicHolidays
            ) {
              setPublicHolidays(
                cached.publicHolidays.data
              );
            }

            if (
              cached.holidayWork
            ) {
              setHolidayWork(
                cached.holidayWork.data
              );
            }

            if (
              cached.workDays
            ) {
              setWorkDays(
                cached.workDays.data
              );
            }
          }
        );

      return () => {
        unsubscribe();

        if (
          realtimeRefreshTimerRef.current
        ) {
          window.clearTimeout(
            realtimeRefreshTimerRef.current
          );

          realtimeRefreshTimerRef.current =
            null;
        }
      };
    },
    [
      year,
      refresh,
    ]
  );

  return {
    people,
    absences,
    publicHolidays,
    holidayWork,
    workDays,

    loading,
    refreshing,
    error,

    refresh,
  };
}